// ─────────────────────────────────────────────────────────────────────
// ACTIVITY SIGN-UP RULES — one definition, two surfaces (#245).
//
// A member signs up from the mobile app; the server is about to decide the same
// things on their behalf. Whether there is room, and therefore whether a
// registration is `confirmed` or `waitlist`, is a PRODUCT rule — if the two
// sides compute it differently the app shows a place the server did not give.
//
// Lifted verbatim from apps/web/src/components/mobile/memberData.ts, which
// re-exports these so its own call sites are unchanged.
// ─────────────────────────────────────────────────────────────────────

/* eslint-disable @typescript-eslint/no-explicit-any */
import { getEloLevel } from './eloLevels.js';

/** Ids cross a JSON boundary as strings or numbers; compare them as strings. */
const same = (a: any, b: any) => a != null && b != null && String(a) === String(b);

/** The member's own registration on an activity (as player OR partner), or null. */
export function memberRegistration(activity: any, memberId: any): any | null {
  if (!activity || memberId == null) return null;
  return (
    (activity.registrations || []).find(
      (r: any) => same(r.playerId, memberId) || same(r.partnerId, memberId),
    ) || null
  );
}

/** Confirmed / waitlist / max for an activity (individual sign-up view). */
export function activityCapacity(a: any): {
  confirmed: number;
  waitlist: number;
  max: number | null;
} {
  const regs = a?.registrations || [];
  const confirmed = regs.filter((r: any) => r.status === 'confirmed').length;
  const waitlist = regs.filter((r: any) => r.status === 'waitlist').length;
  const max = a?.config?.maxPlayers ?? null;
  return { confirmed, waitlist, max: max != null ? Number(max) : null };
}

/**
 * Whether the simple INDIVIDUAL sign-up applies. Pair-, KM- and club-based
 * formats need a partner/class/club and use the full activity page.
 */
export function supportsIndividualSignup(a: any): boolean {
  const tt = String(a?.config?.tournamentType || '');
  if (tt === 'klubbmesterskap' || tt.startsWith('club_')) return false;
  if (a?.config?.teamSignupMode === 'pairs') return false;
  return true;
}

/**
 * Where a new registration lands: `confirmed` while there is room, otherwise
 * the waitlist. No max configured means unlimited.
 *
 * Computed from the activity as it is AT THAT MOMENT, which is why the server
 * must hold the activity's lock across read-decide-write: two members taking
 * the last place both read `confirmed < max` and both become confirmed.
 */
export function registrationStatusFor(activity: any): 'confirmed' | 'waitlist' {
  const { confirmed, max } = activityCapacity(activity);
  return max == null || confirmed < max ? 'confirmed' : 'waitlist';
}

/** Drops the member's registration, whether they hold it as player or partner. */
export function withoutMemberRegistration(activity: any, memberId: any): any[] {
  return (activity?.registrations || []).filter(
    (r: any) => !same(r.playerId, memberId) && !same(r.partnerId, memberId),
  );
}

/**
 * The activity's level requirement, against a member's level.
 *
 * `levelMin` / `levelMax` are the club's, and either may be absent (open at
 * that end). Lifted from MobileActivities' own `levelOk`, unchanged.
 */
export function meetsActivityLevel(activity: any, memberLevel: number): boolean {
  const min = activity?.config?.levelMin;
  const max = activity?.config?.levelMax;
  if (min != null && memberLevel < Number(min)) return false;
  if (max != null && memberLevel > Number(max)) return false;
  return true;
}

/** The member's level, from their rating. */
export function memberLevelOf(elo: unknown): number {
  return getEloLevel(Math.round(Number(elo) || 0)).level;
}

export type RegistrationRefusal = 'level' | 'format';

/**
 * THE COMPLETE ELIGIBILITY RULE — may this member sign themselves up?
 *
 * One function, because the rule has two halves and shipping only one of them
 * server-side is how a member gets in through a door the app does not show
 * them. The app already refuses both: a format needing a partner hands over to
 * the activity page, and a member outside the level range is told «Utenfor
 * nivåkravet» instead of a button. Those were browser-only checks until #245;
 * the server applies the same two now, from the same code.
 *
 * Capacity is deliberately NOT here: being full is not ineligibility — it is
 * what puts a registration on the waitlist (`registrationStatusFor`).
 */
export function memberRegistrationRefusal(
  activity: any,
  memberLevel: number,
): RegistrationRefusal | null {
  if (!supportsIndividualSignup(activity)) return 'format';
  if (!meetsActivityLevel(activity, memberLevel)) return 'level';
  return null;
}

// ── IS SIGN-UP EVEN OPEN? ────────────────────────────────────────────

export type SignupClosedReason = 'status' | 'closed_flag' | 'deadline';

/**
 * Calendar minutes between two venue-local (date, minute-of-day) pairs.
 *
 * Pure date arithmetic on `YYYY-MM-DD` — `Date.UTC` is used only to count days
 * between two plain dates, so no time zone enters. That matters: the browser
 * runs in Europe/Oslo and the API in a UTC container, and converting either end
 * to an instant would make "closes 24 hours before start" mean different
 * moments on the two sides.
 */
export function minutesBetweenLocal(
  fromDate: string,
  fromMinutes: number,
  toDate: string,
  toMinutes: number,
): number {
  const day = (d: string) => {
    const [y, m, dd] = String(d).split('-').map(Number);
    return Date.UTC(y || 1970, (m || 1) - 1, dd || 1);
  };
  const days = (day(toDate) - day(fromDate)) / 86400000;
  return days * 1440 + (toMinutes - fromMinutes);
}

/**
 * Why sign-up is closed, or null when it is open.
 *
 * THE COMPLETE RULE, lifted from `isActivityOpen`, which is what decides
 * whether the member is shown an activity at all. A command that accepted a
 * sign-up for something the app does not list is a door held open behind the
 * UI — the same defect as enforcing the format but not the level.
 *
 * `minutesUntilStart` is null when the activity has no time set; such an
 * activity is open (there is no deadline to be past). The caller computes it,
 * because the two surfaces resolve "when does this start" differently — the
 * browser from its own clock, the server in venue-local time.
 */
export function activitySignupClosedReason(
  activity: any,
  minutesUntilStart: number | null,
): SignupClosedReason | null {
  if (!activity) return 'status';
  if (['cancelled', 'confirmed', 'completed', 'done'].includes(activity.status)) return 'status';
  if (activity.completedAt || activity.sessionStarted || activity.signupClosed) {
    return 'closed_flag';
  }
  if (minutesUntilStart == null) return null; // no time set — nothing to be late for
  const h =
    activity.config?.signupCloseHours != null
      ? Math.max(0, Number(activity.config.signupCloseHours) || 0)
      : 24;
  return minutesUntilStart > h * 60 ? null : 'deadline';
}

// ── WHEN DOES IT START? ──────────────────────────────────────────────
//
// FOUR SOURCES, IN ORDER, and the order is the rule. Lifted verbatim from
// memberData, which re-exports it.
//
// The server needs the whole chain, not just the first link: an activity whose
// time is configured but whose courts are not booked yet still has a start, and
// therefore still has a sign-up deadline. Reading only the booking rows made
// such an activity look unscheduled — open for ever, however long past its
// deadline it was.

export interface ActivityWhen {
  date: string | null;
  start: string | null;
  end: string | null;
  /** Number of calendar days the activity spans (0 when unscheduled). */
  days: number;
}

export function activityWhen(a: any, bookings: any[] = []): ActivityWhen {
  const mine = (bookings || []).filter((b: any) => b.seriesId && b.seriesId === a?.id && b.date);
  if (mine.length) {
    const dates = [...new Set(mine.map((b: any) => b.date as string))].sort();
    const starts = mine
      .map((b: any) => b.startTime)
      .filter(Boolean)
      .sort();
    const ends = mine
      .map((b: any) => b.endTime)
      .filter(Boolean)
      .sort();
    return {
      date: dates[0],
      start: starts[0] || null,
      end: ends[ends.length - 1] || null,
      days: dates.length,
    };
  }
  const room =
    a?.bookingDraft?.slotMode === 'standalone' ? (a.bookingDraft.slotRooms || [])[0] : null;
  if (room?.singleDate) {
    return {
      date: room.singleDate,
      start: room.startTime || null,
      end: room.endTime || null,
      days: 1,
    };
  }
  let d: string | null = a?.config?.activityDate || null;
  let t: string | null = a?.config?.activityTime || null;
  let e: string | null = a?.config?.activityEndTime || null;
  if (!d && !t && a?.config?.p24Snapshot?.days) {
    const pd = (a.config.p24Snapshot.days || [])
      .filter((x: any) => x && x.date)
      .sort((x: any, y: any) => x.date.localeCompare(y.date));
    if (pd[0]) {
      d = pd[0].date;
      t = pd[0].startTime || null;
      e = pd[0].endTime || null;
    }
  }
  return { date: d, start: t, end: e, days: d ? 1 : 0 };
}

// ── AUTO-CANCEL: «too few signed up» ─────────────────────────────────

/**
 * Confirmed PLAYERS, not confirmed registrations — a registration with a
 * partner brings two people.
 */
export function confirmedPlayerCount(activity: any): number {
  return ((activity?.registrations as any[]) || [])
    .filter((r) => r?.status === 'confirmed')
    .reduce((n: number, r: any) => n + (r?.partnerId ? 2 : 1), 0);
}

/**
 * How long before the start sign-up stops counting, in minutes.
 *
 * A club may set its own «N days/hours before start»; otherwise the deadline is
 * when sign-up closes.
 */
export function autoCancelDeadlineMinutes(activity: any): number {
  const cfg = activity?.config ?? {};
  if (cfg.minDeadlineBefore != null) {
    const n = Math.max(0, Number(cfg.minDeadlineBefore) || 0);
    return n * (cfg.minDeadlineUnit === 'hours' ? 60 : 1440);
  }
  const h = cfg.signupCloseHours != null ? Math.max(0, Number(cfg.signupCloseHours) || 0) : 24;
  return h * 60;
}

export type AutoCancelRefusal =
  'not_pending' | 'no_minimum' | 'no_start' | 'deadline_not_passed' | 'minimum_reached';

/**
 * Why this activity may NOT be auto-cancelled, or null when it may.
 *
 * THE COMPLETE RULE the browser applies before proposing a cancellation, lifted
 * so the server can refuse a request that does not meet it. A cancellation
 * command that trusted a client-supplied id list would let a stale or wrong
 * client cancel an activity that is perfectly healthy — and cancellation
 * refunds people.
 *
 * `minutesUntilStart` is null when the activity has no resolvable start; the
 * caller computes it, because the two surfaces resolve time differently (the
 * browser from its own clock, the server in venue-local terms).
 */
export function autoCancelRefusal(
  activity: any,
  minutesUntilStart: number | null,
  confirmedPlayers: number,
): AutoCancelRefusal | null {
  const status = activity?.status;
  if (status != null && status !== 'awaiting_signup') return 'not_pending';
  const minP = activity?.config?.minPlayers;
  if (minP == null) return 'no_minimum';
  if (minutesUntilStart == null) return 'no_start';
  if (minutesUntilStart > autoCancelDeadlineMinutes(activity)) return 'deadline_not_passed';
  if (confirmedPlayers >= Number(minP)) return 'minimum_reached';
  return null;
}
/* eslint-enable @typescript-eslint/no-explicit-any */
